import { useState } from "react"
import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { ChevronRight, LayoutDashboard, MoreHorizontal, Workflow } from "@/components/icons"
import { AppSidebar } from "@/components/layouts/app-layout/app-sidebar"
import { SidebarSearchButton } from "@/components/sidebar/sidebar-search-button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { SidebarGroup, SidebarMenu, SidebarMenuItem } from "@/components/ui/sidebar"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useRoutes } from "@/contexts/use-routes"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { openCanvasInSidebar } from "@/features/canvas/utils/canvas-sidebar"
import { ChatHistory } from "@/features/chat/components/chat-history"
import { StartNewChatButton } from "@/features/chat/components/start-new-chat-button"
import { useStartNewChat } from "@/features/chat/hooks/use-start-new-chat"
import { DashboardsFolder } from "@/features/dash/components/dashboards-folder"
import { DocumentsFolder } from "@/features/documents/components/documents-folder"
import { ProjectsFolder } from "@/features/projects/components/projects-folder"
import { WorkflowsFolder } from "@/features/workflow-editor/components/workflows-folder"
import { useWorkflowsEnabled } from "@/hooks/use-route-overrides"
import { cn } from "@/lib/utils"
import { DocumentService } from "@/services/document-service"
import { SettingsService } from "@/services/settings-service"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"

type ExplorerMoreItem = {
  key: string
  icon: ReactNode
  label: string
  onSelect: () => void
}

// "More" flyout — opens a popover beside the rail (like ChatGPT's More) listing
// the secondary modules as destinations, rather than expanding inline.
function ExplorerMoreSection({ items }: { items: ExplorerMoreItem[] }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          "group/nav-section flex h-9 w-full items-center gap-2 rounded-lg px-2 transition-colors duration-150",
          "hover:bg-foreground/5",
          open && "bg-foreground/5"
        )}
      >
        <MoreHorizontal className="text-sidebar-foreground size-4 shrink-0" />
        <span className="text-sidebar-foreground flex-1 text-left text-sm font-semibold">
          {t("sidebar.more", { defaultValue: "More" })}
        </span>
        <ChevronRight className="text-sidebar-foreground size-4 shrink-0 opacity-60" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1" side="right" sideOffset={8}>
        {items.map((item) => (
          <button
            key={item.key}
            className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150"
            type="button"
            onClick={() => {
              setOpen(false)
              item.onSelect()
            }}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">{item.icon}</span>
            <span className="truncate">{item.label}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export function AIExplorerSidebar() {
  const { state, isMobile } = useSidebar()
  const { routes } = useRoutes()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const sessionType = (() => {
    const matchedRoute = routes
      .filter((route) => pathname.startsWith(route.path))
      .sort((a, b) => b.path.length - a.path.length)
      .at(0)

    const resolveRouteSessionType = (routeId?: string, overrideSessionType?: unknown) => {
      if (typeof overrideSessionType === "string" && overrideSessionType.trim()) {
        return overrideSessionType
      }
      return routeId ?? "free"
    }

    if (matchedRoute) {
      return resolveRouteSessionType(matchedRoute.id, matchedRoute.overrides?.overrideSessionType)
    }

    const defaultExplorerRoute = routes.find((route) => route.component === "free")
    if (defaultExplorerRoute) {
      return resolveRouteSessionType(
        defaultExplorerRoute.id,
        defaultExplorerRoute.overrides?.overrideSessionType
      )
    }

    return routes[0]?.id ?? "free"
  })()
  // Project and document pages have no page-level useChatHistory, so this
  // sidebar owns route sync there; on chat pages the page does (#3515).
  const pathSegments = pathname.split("/").filter(Boolean)
  const ownsRouteSync = pathSegments.includes("projects") || pathSegments.includes("documents")
  const isWorkflowsFeatureEnabled = useWorkflowsEnabled()
  const isDashEnabled = routes.some((r) => r.id === "dash" && r.enabled)

  const moreItemCandidates: (ExplorerMoreItem | null)[] = [
    isDashEnabled
      ? {
          key: "dash",
          icon: <LayoutDashboard className="size-4" />,
          label: t("dash.landing.sidebar.title", { defaultValue: "Dashboards" }),
          onSelect: () => {
            void navigate("/dash")
          },
        }
      : null,
    isWorkflowsFeatureEnabled
      ? {
          key: "flows",
          icon: <Workflow className="size-4" />,
          label: t("workflowLanding.title", { defaultValue: "Flows" }),
          onSelect: () => {
            void navigate("/flows")
          },
        }
      : null,
  ]
  const moreItems = moreItemCandidates.filter((item): item is ExplorerMoreItem => item !== null)
  const { gptModel: selectedModel } = useBaseSettings({ sessionType })

  const explorerStore = useAIExplorerStore(sessionType)
  const { handleStartNewChat } = useStartNewChat({
    useChatStore: () => explorerStore,
    sessionType,
  })

  const [globalSearch, setGlobalSearch] = useState("")

  const currentRoute = routes.find((r) => r.id === sessionType)

  if (!currentRoute) {
    return null
  }

  const handleCreateDocumentFromSidebar = async () => {
    try {
      const settingsService = SettingsService.getInstance()
      const created = await DocumentService.createDocument(
        {
          title: t("documents.untitled", { defaultValue: "Untitled document" }),
          content: "",
          contentFormat: "markdown",
        },
        settingsService.token
      )

      handleStartNewChat()

      openCanvasInSidebar(
        {
          documentId: created.id,
          title: created.title,
          content: "",
          versions: [],
          selectedModelId: selectedModel.id,
          lastSavedAt: created.updated ?? null,
        },
        {
          title: created.title,
        }
      )
    } catch (error) {
      toast.error(
        t("documents.create_failed", {
          defaultValue: "Failed to create document",
        }),
        {
          description: error instanceof Error ? error.message : undefined,
        }
      )
    }
  }

  const isCollapsed = state === "collapsed" && !isMobile

  return (
    <AppSidebar onLogoClick={handleStartNewChat}>
      <SidebarGroup className="bg-sidebar sticky top-0 z-40 gap-1 p-2 pb-0">
        <SidebarMenu>
          <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
            <StartNewChatButton iconOnly={isCollapsed} onStartNewChatClick={handleStartNewChat} />
          </SidebarMenuItem>
        </SidebarMenu>
        {!isCollapsed && (
          <SidebarSearchButton searchValue={globalSearch} onSearchChange={setGlobalSearch} />
        )}
      </SidebarGroup>

      <SidebarGroup
        className={cn("flex flex-col gap-1 px-2 pt-1 pb-2", isCollapsed ? "items-center" : "")}
      >
        <ProjectsFolder
          defaultExpanded={false}
          globalSearchTerm={globalSearch}
          sessionType={sessionType}
        />
        <DocumentsFolder
          defaultExpanded={false}
          globalSearchTerm={globalSearch}
          onCreateDocument={() => void handleCreateDocumentFromSidebar()}
        />
        {moreItems.length > 0 &&
          (isCollapsed || globalSearch ? (
            <>
              {isDashEnabled && <DashboardsFolder globalSearchTerm={globalSearch} />}
              {isWorkflowsFeatureEnabled && <WorkflowsFolder globalSearchTerm={globalSearch} />}
            </>
          ) : (
            <ExplorerMoreSection items={moreItems} />
          ))}
        <ChatHistory
          globalSearchTerm={globalSearch}
          sessionType={sessionType}
          syncRoute={ownsRouteSync}
          useChatStore={() => explorerStore}
        />
      </SidebarGroup>
    </AppSidebar>
  )
}
